import { IPC_CHANNELS } from './constants';

/**
 * Sprint 63 (Fase 5) — El espigón de autorización: qué oficio puede tocar qué.
 *
 * El sistema tenía **dos** roles (`dueño`, `mesero`) y el negocio tiene **cuatro
 * oficios**. La autorización era binaria: `safeHandleConDueno` (≈89 canales) o
 * `safeHandleConSesion` (cualquier sesión). Con cuatro roles eso no alcanza, y esparcir
 * `if (rol === 'caja')` por los handlers es la forma conocida de dejar un hueco: nadie
 * puede auditar 86 condiciones repartidas.
 *
 * Aquí está la matriz completa, en un archivo que se lee de corrido. Dos propiedades que
 * la hacen auditable:
 *
 *  1. **El nombre del canal ES el par (sección, acción).** `productos:crear` ya lo dice,
 *     así que no hay una segunda taxonomía que mantener sincronizada — y no hay canal que
 *     se pueda "olvidar de declarar" sin que se note.
 *  2. **Fail-closed.** Un canal cuya sección no esté declarada aquí queda **sólo para el
 *     dueño**. Si alguien agrega un canal y no lo clasifica, el peor caso es que un rol
 *     no pueda usarlo; nunca que un mesero cobre. Y el test guardián
 *     (`sprint63Permisos.test.ts`) falla hasta que se declare.
 *
 * **Estado: aplicada.** Los 144 canales que llevaban autorización binaria pasan por
 * `safeHandleConPermiso`; las 12 excepciones (login, onboarding, branding pre-sesión y
 * los dos canales con 2FA del dueño) están declaradas una por una en
 * `sprint63PermisosAplicados.test.ts`, que falla si alguien vuelve al modelo viejo o
 * registra un canal sin autorización.
 */

// ─────────────────────────────────────────────────────────────
// Los cuatro oficios
// ─────────────────────────────────────────────────────────────

export const ROLES = ['dueño', 'caja', 'cocina', 'mesero'] as const;
export type Rol = (typeof ROLES)[number];

/**
 * Los que se pueden asignar al crear un usuario.
 *
 * Se abrieron los cuatro cuando los handlers empezaron a usar la matriz (segunda mitad
 * del Fase 5): antes, un usuario de cocina habría tenido todo lo que alcanzaba
 * `safeHandleConSesion` —el POS incluido—, o sea lo contrario de lo que el negocio pidió.
 *
 * El guardián `sprint63PermisosAplicados.test.ts` es lo que sostiene esa afirmación: si
 * un canal volviera al modelo binario, la suite falla.
 */
export const ROLES_ASIGNABLES: readonly Rol[] = ROLES;

/** Cómo se llama cada oficio en pantalla. */
export const NOMBRE_ROL: Record<Rol, string> = {
  dueño: 'Dueño',
  caja: 'Caja y domicilio',
  cocina: 'Cocina',
  mesero: 'Mesero',
};

/** Qué hace cada uno, en una línea, para la pantalla de usuarios. */
export const DESCRIPCION_ROL: Record<Rol, string> = {
  dueño: 'Todo: catálogo, inventario, reportes, configuración y cierre del día.',
  caja: 'Cobra, hace el corte, atiende domicilio y ve el tablero del negocio.',
  cocina: 'Ve lo que hay que preparar y marca cuando está listo para llevar a la mesa.',
  mesero: 'Toma la orden en la mesa, la manda a cocina y avisa a caja para cobrar.',
};

/**
 * A dónde entra cada oficio al iniciar sesión.
 *
 * `/dashboard` no es una sola pantalla: `Dashboard.tsx` ya rendereaba una versión propia
 * para el mesero desde el Sprint 31 —"qué mesa toca atender", con las que piden cuenta o
 * tienen platos listos—, así que ahí es donde debe caer. Mandarlo a `/mesas` habría sido
 * un retroceso: la rejilla de mesas no prioriza nada.
 *
 * Cocina sí va directo a su cola: para quien prepara comida, un tablero de ventas es
 * ruido, y de hecho no puede consultarlo.
 */
export const RUTA_INICIAL: Record<Rol, string> = {
  dueño: '/dashboard',
  caja: '/dashboard',
  cocina: '/cocina',
  mesero: '/dashboard',
};

// ─────────────────────────────────────────────────────────────
// Las secciones declaradas (fail-closed: lo que no esté aquí es del dueño)
// ─────────────────────────────────────────────────────────────

/**
 * Toda sección de canal que existe hoy. El guardián falla si aparece una nueva sin
 * declararla: es la forma de que agregar un canal obligue a decidir quién lo usa.
 */
export const SECCIONES_DECLARADAS = [
  /**
   * Sprint 136 — Presencia y chat interno.
   *
   * Va para **los cuatro oficios**, y es de las pocas veces que eso es lo correcto sin matices:
   * pedir ayuda cuando uno se atora no es un privilegio del dueño. Un mesero que no encuentra
   * cómo mandar algo a cocina tiene el mismo problema que la dueña con las promociones.
   *
   * No expone dinero ni catálogo: sólo quién está conectado, desde qué pantalla, y mensajes entre
   * la gente del mismo negocio. El aislamiento entre negocios lo da la base, que es distinta.
   */
  'presencia',
  'chat',
  /**
   * Sprint 91 — Cómo le dicen los clientes a cada producto ("coca grande", "espadas").
   *
   * Sección propia y no parte de `productos` a propósito: leer y enseñar un apodo lo hace quien
   * **toma pedidos**, y dárselo desde `productos` le habría abierto también el catálogo y los
   * precios. Borrar un apodo sí queda sólo para el dueño, por fail-closed.
   */
  'apodos',
  'auth', // entrar y salir
  'backup', // respaldos de la base
  'categorias', // agrupación de botones del POS
  'avisos', // Sprint 163 — aviso "va en camino" al cliente (sólo dueño, fail-closed)
  'sin-concluir', // Sprint 164 — pedidos de días pasados sin concluir (sólo dueño, fail-closed)
  /**
   * Sprint 167 — Privacidad de los clientes. Sección propia y NO dentro de `clientes`: caja tiene
   * `clientes:*` para buscar al cliente que llama, y ese comodín le habría dado también borrar a una
   * persona de la base. No se declara para ningún oficio: sólo el dueño, por fail-closed.
   */
  'privacidad',
  'cierre-dia', // cierre de inventario del día
  /**
   * Sprint 157 — El reporte de ingresos para la contadora.
   *
   * Sección propia y NO dentro de `reportes`: el puesto de caja tiene `reportes:*` para ver el
   * tablero del día, y ese comodín le habría dado también el papel que sale del negocio con los
   * ingresos del mes. No se declara para ningún oficio: sólo el dueño, por fail-closed.
   */
  'contabilidad',
  /**
   * Sprint 170 — "Cómo va el negocio": qué deja dinero, qué no se vende, qué días son flojos.
   *
   * Sección propia y NO dentro de `reportes`, por el mismo motivo que `contabilidad`: el puesto de
   * caja tiene `reportes:*` para el tablero del día, y ese comodín le habría dado también la
   * estrategia del negocio. No se declara para ningún oficio: sólo el dueño, por fail-closed.
   */
  'estadisticas',
  'clientes', // datos de quien pide a domicilio
  'config', // preferencias del sistema
  'corte', // corte de caja
  'error', // reporte de errores del propio sistema
  'impresion', // cola de impresión del agente
  'impresoras', // qué impresora usa cada destino
  'insumos', // ingredientes e inventario
  'mesas', // salón
  'movimientos-caja', // entradas y salidas de efectivo
  'negocio', // datos y branding
  'onboarding', // asistente de instalación (público por necesidad)
  'paquetes', // combinaciones con precio propio
  'pedidos', // la operación de venta
  'productos', // el menú
  'promociones', // descuentos y regalos
  'propinas', // reparto entre el personal
  'reportes', // ventas y estado del día
  'smtp', // correo para reportes de error
  'ticket', // comprobante del cliente
  'ubicacion', // mapa de domicilio
  'unidades', // catálogo de unidades de medida
  'update', // actualización de la app
  'usuarios', // quién puede entrar
  // Sprint 77 — A domicilio: el dinero.
  'zonas', // zonas de reparto y su tarifa
  /**
   * Devoluciones de pedidos a domicilio que no se entregaron (DT-072).
   *
   * Declarada **a propósito sin asignar a ningún oficio**: es dinero saliendo de la caja, así que
   * la matriz la deja sólo para el dueño sin necesidad de una excepción escrita a mano. Si
   * mañana el negocio quiere que la caja también pueda, se agrega ahí y se ve en el diff.
   */
  'devoluciones',
  /**
   * Sprint 139 — Corregir la forma de pago de un cobro ya registrado.
   *
   * Sección propia y **fuera de `pedidos`** por una razón concreta: `caja` tiene el comodín
   * `pedidos:*`, así que meterla ahí se la habría dado sin que nadie lo decidiera. Y no es
   * vender: es deshacer un cobro que ya está en la caja — la acción con la que se taparía un
   * faltante de efectivo.
   *
   * Nace sólo para el dueño (fail-closed). El negocio se la da a la caja con el interruptor que
   * ya existe para *"la caja puede cancelar"*, en vez de con uno nuevo: es la misma decisión.
   */
  'correccion-pago',
] as const;

export type Seccion = (typeof SECCIONES_DECLARADAS)[number];

// ─────────────────────────────────────────────────────────────
// La matriz
// ─────────────────────────────────────────────────────────────

/**
 * Reglas por oficio. Cada una es un canal exacto (`pedidos:cobrar`) o una sección
 * completa (`productos:*`).
 *
 * Se escribe en positivo —lo que cada uno PUEDE— porque una lista de prohibiciones no se
 * puede auditar: siempre falta una.
 */
/** Sprint 172 (DT-209) — Los canales de pedidos de la caja, enumerados. Ver la nota en `caja`. */
export const PEDIDOS_DE_CAJA = [
  'pedidos:abrir-comprobante',
  'pedidos:actualizar-envio',
  'pedidos:adjuntar-comprobante',
  'pedidos:agregar-items',
  'pedidos:asignar-repartidor',
  'pedidos:bandeja',
  'pedidos:cancelar',
  'pedidos:cancelar-item',
  'pedidos:cerrar-entregado-pagado',
  'pedidos:cobrar',
  'pedidos:cobrar-diferencia',
  'pedidos:confirmar-pago-verificado',
  'pedidos:crear',
  'pedidos:dividir',
  'pedidos:division',
  'pedidos:domicilios-entregados-hoy',
  'pedidos:enviar-cocina',
  'pedidos:juntar-mesas',
  'pedidos:listar',
  'pedidos:listar-mostrador',
  'pedidos:listar-por-estado',
  'pedidos:marcar-cuenta',
  'pedidos:marcar-empaquetado',
  'pedidos:marcar-entregado',
  'pedidos:marcar-entregado-mesa',
  'pedidos:marcar-entregado-mostrador',
  'pedidos:marcar-listo',
  'pedidos:marcar-no-entregado',
  'pedidos:mover-mesa',
  'pedidos:obtener',
  'pedidos:prorrogar-pago',
  'pedidos:reunificar',
  'pedidos:separar-mesas',
  'pedidos:sugerencia-envio',
  'pedidos:tablero-domicilios',
  'pedidos:validar-carrito',
  'pedidos:ventas-del-dia',
  // Sprint 172 — Reasignar las mesas de un mesero que se fue, su globo y la bitácora (con PIN).
  'pedidos:entregar-turno',
  'pedidos:entregas-recientes',
  'pedidos:bitacora-turnos',
] as const;

const MATRIZ: Record<Rol, readonly string[]> = {
  /** El dueño no se enumera: puede todo, incluido lo que se agregue mañana. */
  dueño: ['*'],

  /**
   * Caja y domicilio. Cobra, cuadra y despacha.
   *
   * Ve el inventario porque el negocio lo pidió explícitamente —*"incluso
   * disponibilidad"*— pero sólo de lectura: cargar o corregir existencias es del dueño.
   * No toca catálogo, usuarios ni configuración, y **no cierra el día** (va con 2FA del
   * dueño desde el Sprint 32).
   */
  caja: [
    'auth:*',
    'error:*',
    // Sprint 136 — Pedir ayuda no es privilegio de nadie.
    'presencia:*',
    'chat:*',
    // Sprint 64 — Su propio PIN, nada más (el handler exige que sea el de la sesión).
    'usuarios:cambiar-pin',
    'negocio:obtener',
    'config:obtener',
    'unidades:listar',
    /**
     * Vender y cobrar. Sprint 172 (DT-209, kit B-01) — **sin comodín.** `pedidos:*` abría a la caja
     * cualquier canal nuevo de pedidos sin que nadie lo decidiera (la entrega de turno, por ejemplo).
     * Esta es exactamente la lista que el comodín daba el 04/11/2026; un canal nuevo de pedidos queda
     * sólo para el dueño hasta que alguien lo agregue aquí a propósito, y `sprint172...` se pone rojo
     * si aparece uno sin decidir.
     */
    ...PEDIDOS_DE_CAJA,
    'ticket:*',
    // Sin comodín: `impresion:codigo-emparejamiento` empareja un agente de impresión, y eso
    // es entregar un token permanente a TODOS los tickets del negocio —desde cualquier
    // máquina y sin caducidad—. Es del dueño. Enumerar aquí es más feo y es correcto.
    'impresion:encolar',
    'impresion:estado',
    'clientes:*',
    'ubicacion:*',
    /**
     * Sprint 77 — Las zonas de reparto, **sólo para leerlas**: al cotizar un envío hay que ver
     * los atajos. Crearlas o cambiar su tarifa es del dueño, porque es fijar un precio.
     */
    'zonas:listar',
    // Catálogo, sólo para poder vender
    'productos:listar',
    /**
     * Sprint 91 — Los apodos con los que el cliente pide. Van con quien toma pedidos porque
     * aprender uno **nace de resolver un renglón**, no de administrar el catálogo: el operador no
     * está configurando nada, está trabajando. `apodos:olvidar` NO va — borrar lo que el sistema
     * aprendió sí es administrar, y eso es del dueño.
     */
    'apodos:listar',
    'apodos:aprender',
    'paquetes:listar',
    'paquetes:obtener',
    'categorias:listar',
    'promociones:listar',
    'promociones:obtener',
    'promociones:simular',
    // Salón: ve y usa, no configura. `mesas:actualizar` NO va — su único consumidor es el
    // formulario de alta/edición de mesas, así que dárselo era dejarle renumerar el salón.
    // El número de mesa es único y es el que la gente dice en voz alta: renumerarlo a media
    // noche descuadra los pedidos abiertos con las mesas físicas.
    'mesas:listar',
    // Caja. `corte:cerrar` NO va: cerrar el corte exige 2FA del dueño desde el Sprint 32
    // (`safeHandleConStepUp`, que no consulta esta matriz). Dárselo aquí sólo lograba que la
    // pantalla de permisos le prometiera al dueño algo que en el piso rebota.
    'corte:abierto',
    'corte:abrir',
    'corte:pdf',
    // Sprint 160 — `corte:jornada` NO va: devuelve el efectivo esperado que la dueña todavía no
    // cuenta, y la caja no lo necesita para vender (crear un pedido ya cambia la jornada solo).
    // Auditoría `cyber` S160, mínimo privilegio.
    'movimientos-caja:*',
    // Tablero y disponibilidad
    'reportes:*',
    'insumos:listar',
    'insumos:obtener',
  ],

  /**
   * Cocina. Ve lo que hay que preparar y avisa cuando está.
   *
   * Deliberadamente **no ve precios ni cobros**: un monitor de cocina suele quedar a la
   * vista de todos, y ahí no tiene por qué estar el dinero del negocio.
   */
  cocina: [
    'auth:*',
    'error:*',
    // Sprint 136 — Pedir ayuda no es privilegio de nadie.
    'presencia:*',
    'chat:*',
    'usuarios:cambiar-pin',
    'negocio:obtener',
    'config:obtener',
    'pedidos:listar',
    'pedidos:listar-por-estado',
    'pedidos:obtener',
    'pedidos:marcar-listo',
    // Sprint 66 — Quien mete la comida en la bolsa es quien avisa que ya está empaquetada.
    'pedidos:marcar-empaquetado',
    'pedidos:marcar-entregado-mesa',
    /**
     * Sprint 111 — El mostrador. Mismo criterio que `marcar-empaquetado`: quien arma la bolsa es
     * quien ve la cola y quien sabe que el cliente ya se fue con ella.
     *
     * **Los importes se anulan en el servidor** con `filtrarDinero` cuando `cocinaVePrecios` está
     * apagado (`main/ipc/pedidosHandlers.ts`). Esto se escribe así, y no *"el tablero no muestra
     * precios"*, porque la primera versión de este comentario afirmaba justo eso — y era una
     * promesa del renderer anotada en el archivo de autoridad: la pantalla sí pintaba el importe
     * de cada tarjeta y la suma de la columna, y por el bridge web se veía cruda en la respuesta
     * HTTP. Lo encontró la auditoría del propio sprint. Aquí sólo se afirma lo que es verificable
     * en el servidor.
     */
    'pedidos:listar-mostrador',
    'pedidos:marcar-entregado-mostrador',
    'impresion:estado',
  ],

  /**
   * Mesero. Toma la orden, la manda a cocina y avisa a caja.
   *
   * **No cobra.** Sí puede unir mesas y dividir cuentas (existían desde el Sprint 29) y
   * marcar que el cliente pidió su cuenta, que es la fricción real del piso: hoy hay que
   * cruzar el salón a avisar.
   *
   * Cancelar un pedido o anular un renglón sigue exigiendo el PIN del dueño (Sprint 9.2),
   * así que esos canales no se le abren aquí.
   */
  mesero: [
    'auth:*',
    'error:*',
    // Sprint 136 — Pedir ayuda no es privilegio de nadie.
    'presencia:*',
    'chat:*',
    'usuarios:cambiar-pin',
    'negocio:obtener',
    'config:obtener',
    // Tomar la orden
    'pedidos:crear',
    'pedidos:listar',
    'pedidos:listar-por-estado',
    'pedidos:obtener',
    'pedidos:enviar-cocina',
    'pedidos:agregar-items',
    'pedidos:marcar-cuenta',
    'pedidos:mover-mesa',
    'pedidos:juntar-mesas',
    'pedidos:separar-mesas',
    'pedidos:dividir',
    'pedidos:reunificar',
    // Sprint 68 — Quien divide la cuenta tiene que poder ver cuántas partes faltan.
    'pedidos:division',
    'pedidos:marcar-entregado-mesa',
    /**
     * Sprint 172 — Entregarle sus mesas a un compañero (los dos ponen su PIN). Ver la bitácora y el
     * globo NO: eso es de quien manda (caja y dueña).
     */
    'pedidos:entregar-turno',
    // Lo que necesita para armar la orden
    'productos:listar',
    /**
     * Sprint 91 — Los apodos con los que el cliente pide. Van con quien toma pedidos porque
     * aprender uno **nace de resolver un renglón**, no de administrar el catálogo: el operador no
     * está configurando nada, está trabajando. `apodos:olvidar` NO va — borrar lo que el sistema
     * aprendió sí es administrar, y eso es del dueño.
     */
    'apodos:listar',
    'apodos:aprender',
    'paquetes:listar',
    'paquetes:obtener',
    'categorias:listar',
    'promociones:listar',
    'promociones:simular',
    'mesas:listar',
    // Mostrarle la cuenta al cliente e imprimir la comanda
    'ticket:*',
    'impresion:encolar',
    'impresion:estado',
  ],
};

// ─────────────────────────────────────────────────────────────
// Los tres ajustes que el negocio sí decide
// ─────────────────────────────────────────────────────────────

/**
 * Sprint 64 — Ajustes del negocio sobre la matriz.
 *
 * Son **tres, curados a mano**, y no una matriz libre de 157 canales × 4 oficios. La razón
 * la puso el arquitecto en el debate: una tabla editable completa es una superficie de
 * error enorme —el día que alguien marque por curiosidad "cocina puede cobrar" nadie lo
 * nota hasta el corte— y encima saca la autorización del código versionado y protegido por
 * tests, para meterla en un dato del cliente.
 *
 * Estos tres, en cambio, son decisiones que el oficio del restaurante justifica y que
 * cambian de negocio en negocio:
 *
 *  - En un local chico, un martes flojo, **no hay cajera**: si el mesero no cobra, el
 *    sistema estorba.
 *  - Si el monitor de cocina está en la barra a la vista de los clientes, **los precios no
 *    van ahí**. Si está en la cocina cerrada, da igual.
 *  - Que la cajera **pueda o no cancelar** depende de cuánta confianza y cuánta rotación
 *    hay. El PIN del dueño se sigue pidiendo en ambos casos (Sprint 9.2).
 */
export interface AjustesPermisos {
  /** El mesero puede cobrar. Default `false`. */
  meseroCobra: boolean;
  /** La cocina ve precios y tickets. Default `false`. */
  cocinaVePrecios: boolean;
  /** La caja puede cancelar pedidos y anular renglones. Default `true`. */
  cajaCancela: boolean;
}

/**
 * Cómo arranca un negocio nuevo. `cajaCancela` viene en `true` porque es lo que espera
 * cualquier restaurante: la cajera cancela un renglón mal capturado sin ir a buscar al dueño
 * (y de todos modos se le pide su PIN).
 */
export const AJUSTES_POR_DEFECTO: AjustesPermisos = {
  meseroCobra: false,
  cocinaVePrecios: false,
  cajaCancela: true,
};

/**
 * Qué aplica cuando NO se pudo leer la configuración del negocio.
 *
 * No es lo mismo que el default de fábrica, y confundirlos fue un hallazgo de auditoría: si
 * el dueño apagó "la caja puede cancelar" y luego hay un fallo de lectura, caer en los
 * defaults le DEVOLVÍA el permiso que él había quitado. Ante duda, todo cerrado.
 */
export const AJUSTES_ANTE_ERROR: AjustesPermisos = {
  meseroCobra: false,
  cocinaVePrecios: false,
  cajaCancela: false,
};

/**
 * Cómo se lee uno de estos ajustes guardado como texto.
 *
 * Vive aquí, en `shared`, porque lo usan el servidor y la pantalla. Tenerlo dos veces ya
 * causó una divergencia: el servidor aceptaba `'true'` y `'1'`, el renderer sólo `'true'`, y
 * con un `'1'` guardado el servidor autorizaba mientras la pantalla escondía el botón.
 */
export function leerAjusteGuardado(valor: string | null | undefined, porDefecto: boolean): boolean {
  if (valor === null || valor === undefined) return porDefecto;
  return valor === 'true' || valor === '1';
}

/** Las claves con las que se guardan en `ConfiguracionSistema`. */
export const CLAVE_AJUSTE: Record<keyof AjustesPermisos, string> = {
  meseroCobra: 'permiso_mesero_cobra',
  cocinaVePrecios: 'permiso_cocina_ve_precios',
  cajaCancela: 'permiso_caja_cancela',
};

/** Qué canales agrega o quita cada ajuste. */
const CANALES_AJUSTE: Record<keyof AjustesPermisos, { rol: Rol; canales: string[] }> = {
  /*
    Sólo los dos canales de cobro: imprimir ya lo tiene por `ticket:*` y meterlo aquí se lo
    quitaría mientras el interruptor esté apagado.

    Sprint 141 — `pedidos:cobrar-diferencia` entra con su hermano. Si el negocio decidió que el
    mesero cobra, no tiene sentido que pueda cobrar una cuenta de $900 y rebote al cobrar los $20
    que el cliente agregó: es el mismo acto, y quedarse a medias con el cliente delante es peor
    que no poder empezar.
  */
  meseroCobra: { rol: 'mesero', canales: ['pedidos:cobrar', 'pedidos:cobrar-diferencia'] },
  cocinaVePrecios: { rol: 'cocina', canales: ['productos:listar', 'ticket:html'] },
  /*
    Sprint 139 — La corrección de forma de pago **NO** viaja con este interruptor.

    Se intentó y la auditoría lo tumbó con tres razones, cada una suficiente:

    1. `cajaCancela` **nace en `true`**, así que "dárselo a la caja con el interruptor que ya
       existe" significaba, en cualquier negocio que no lo haya tocado, **encendido el día del
       despliegue**. El comentario que decía "nace fail-closed" era falso en producción.
    2. La etiqueta que el dueño lee promete *"en ambos casos se le sigue pidiendo tu PIN"* — y
       eso no aplicaba al canal nuevo. Se estaría autorizando una cosa leyendo otra.
    3. Y no es la misma decisión: **cancelar bloquea una venta que todavía no pasó; corregir
       reescribe una que ya está en la caja.**

    Queda sólo para el dueño. El día que el negocio quiera dársela a la caja, será con su propio
    interruptor y se verá en el diff.
  */
  cajaCancela: { rol: 'caja', canales: ['pedidos:cancelar', 'pedidos:cancelar-item'] },
};

/**
 * Sprint 139 — La misma tabla, expuesta para que un guardián pueda comprobarla.
 *
 * Existe porque el reparto de canales por interruptor es justo donde se coló el error que tumbó
 * la primera versión del Sprint 139: dar la corrección de pagos con `cajaCancela`, que **nace
 * encendido**. Un test puede recorrerla y exigir que cierto canal no esté ahí.
 */
export const CANALES_AJUSTE_PARA_TESTS = CANALES_AJUSTE;

// ─────────────────────────────────────────────────────────────
// La misma matriz, en palabras del negocio
// ─────────────────────────────────────────────────────────────

/**
 * Sprint 64 — Lo que el dueño ve en Configuración → Permisos.
 *
 * Es la matriz **traducida**, no una copia. Cada renglón guarda el canal real que lo
 * representa y la pantalla pregunta a `puedeUsarCanal`, así que la tabla no puede mentir:
 * el día que alguien cambie la matriz, la pantalla cambia sola. Una segunda lista escrita
 * a mano se desincronizaría en el primer sprint y sería peor que no tener pantalla —
 * enseñaría permisos que no son los que se aplican.
 *
 * Son 18 renglones de las 27 secciones a propósito: `auth`, `error`, `update`, `onboarding`
 * y compañía son plomería, no decisiones del negocio, y llenar la tabla de renglones que
 * nadie entiende es la manera más segura de que no la lea nadie.
 */
export interface TareaDelNegocio {
  /** Cómo se llama en la cocina, no en el código. */
  titulo: string;
  /** Canal real que decide. La tabla se pinta preguntando por éste. */
  canal: string;
  /** Agrupador visual. */
  grupo: 'Atender' | 'Dinero' | 'Cocina' | 'Inventario' | 'El negocio';
  /**
   * Además del oficio, pide el segundo factor del dueño (Sprint 32). Se muestra en la tabla
   * porque si no, el dueño lee "sí puede" y en el piso le sale que no.
   */
  pideCodigo?: boolean;
}

export const TAREAS_DEL_NEGOCIO: readonly TareaDelNegocio[] = [
  /**
   * Sprint 122 — La bandeja une los tres canales, así que también une lo que se ve en ellos:
   * un domicilio trae nombre, teléfono y dirección del cliente. Es de quien atiende, no de
   * quien cocina — el monitor de cocina suele verse desde el salón.
   */
  { grupo: 'Atender', titulo: 'Ver todos los pedidos en una sola bandeja', canal: 'pedidos:bandeja' },
  { grupo: 'Atender', titulo: 'Ver y abrir mesas', canal: 'mesas:listar' },
  { grupo: 'Atender', titulo: 'Tomar un pedido', canal: 'pedidos:crear' },
  { grupo: 'Atender', titulo: 'Agregar platos a una mesa', canal: 'pedidos:agregar-items' },
  { grupo: 'Atender', titulo: 'Juntar mesas y separar cuentas', canal: 'pedidos:dividir' },
  { grupo: 'Atender', titulo: 'Avisar que la mesa pidió su cuenta', canal: 'pedidos:marcar-cuenta' },

  { grupo: 'Cocina', titulo: 'Mandar el pedido a la cocina', canal: 'pedidos:enviar-cocina' },
  { grupo: 'Cocina', titulo: 'Ver la pantalla de cocina', canal: 'pedidos:listar-por-estado' },
  { grupo: 'Cocina', titulo: 'Avisar que un plato ya está listo', canal: 'pedidos:marcar-listo' },
  { grupo: 'Cocina', titulo: 'Marcar que ya se entregó en la mesa', canal: 'pedidos:marcar-entregado-mesa' },
  { grupo: 'Cocina', titulo: 'Ver quién espera en el mostrador', canal: 'pedidos:listar-mostrador' },
  { grupo: 'Cocina', titulo: 'Marcar que el cliente ya se llevó su pedido', canal: 'pedidos:marcar-entregado-mostrador' },

  { grupo: 'Dinero', titulo: 'Cobrar', canal: 'pedidos:cobrar' },
  /*
    Sprint 141 — La capacidad se nombra para que el dueño pueda verla y decidirla.

    Le llegaba a la caja por el comodín `pedidos:*` sin que nadie lo hubiera elegido, y no
    aparecía en esta tabla: un permiso que existe y no se puede leer es un permiso que nadie
    revisa. Es el mismo argumento por el que el Sprint 137 sacó a la luz quién firma el crédito.
  */
  { grupo: 'Dinero', titulo: 'Cobrar lo que falta de un pedido ya cobrado', canal: 'pedidos:cobrar-diferencia' },
  { grupo: 'Dinero', titulo: 'Imprimir el ticket del cliente', canal: 'ticket:imprimir' },
  { grupo: 'Dinero', titulo: 'Cancelar un pedido o quitar un plato', canal: 'pedidos:cancelar' },
  { grupo: 'Dinero', titulo: 'Abrir la caja del turno', canal: 'corte:abrir' },
  { grupo: 'Dinero', titulo: 'Cerrar el corte de caja', canal: 'corte:cerrar', pideCodigo: true },
  { grupo: 'Dinero', titulo: 'Decir cuánto se contó en la caja de ayer', canal: 'corte:declarar-conteo', pideCodigo: true },
  { grupo: 'Dinero', titulo: 'Registrar entradas y salidas de efectivo', canal: 'movimientos-caja:registrar' },
  { grupo: 'Dinero', titulo: 'Repartir las propinas', canal: 'propinas:ejecutar-reparto' },
  { grupo: 'Dinero', titulo: 'Ver los reportes de venta', canal: 'reportes:ventas-dia' },
  { grupo: 'Dinero', titulo: 'Ver cómo va el negocio y qué conviene vender', canal: 'estadisticas:tablero' },
  { grupo: 'Dinero', titulo: 'Ver las ventas del día y corregir una', canal: 'pedidos:ventas-del-dia' },
  { grupo: 'Dinero', titulo: 'Cerrar el día', canal: 'cierre-dia:ejecutar', pideCodigo: true },

  { grupo: 'Inventario', titulo: 'Ver qué hay disponible', canal: 'insumos:listar' },
  { grupo: 'Inventario', titulo: 'Cargar la mercancía del día', canal: 'insumos:carga-diaria' },
  { grupo: 'Inventario', titulo: 'Registrar merma (lo que se cayó o se quemó)', canal: 'insumos:registrar-merma' },

  { grupo: 'El negocio', titulo: 'Ver precios y el menú completo', canal: 'productos:listar' },
  { grupo: 'El negocio', titulo: 'Cambiar precios, platos y paquetes', canal: 'productos:actualizar' },
  { grupo: 'El negocio', titulo: 'Crear promociones', canal: 'promociones:crear' },
  { grupo: 'El negocio', titulo: 'Dar de alta gente y cambiar PINes', canal: 'usuarios:crear' },
  { grupo: 'El negocio', titulo: 'Cambiar la configuración', canal: 'config:actualizar' },
  { grupo: 'El negocio', titulo: 'Respaldar y restaurar', canal: 'backup:crear' },
];

/** Los tres interruptores, con la consecuencia escrita al lado. */
export interface AjusteVisible {
  clave: keyof AjustesPermisos;
  etiqueta: string;
  /** Qué pasa si se enciende. En una línea y sin jerga. */
  consecuencia: string;
  /** Cuándo tiene sentido encenderlo, del oficio. */
  cuando: string;
}

export const AJUSTES_VISIBLES: readonly AjusteVisible[] = [
  {
    clave: 'meseroCobra',
    etiqueta: 'El mesero también puede cobrar',
    consecuencia: 'El mesero podrá cerrar la cuenta e imprimir el ticket. Todo lo cobrado sigue cayendo al corte del día.',
    cuando: 'Útil si hay turnos sin cajera. Si siempre hay alguien en la caja, déjalo apagado: menos manos en el dinero es menos que investigar.',
  },
  {
    clave: 'cocinaVePrecios',
    etiqueta: 'La cocina puede ver precios',
    consecuencia: 'La pantalla de cocina mostrará el menú con precios y podrá ver el ticket.',
    cuando: 'Déjalo apagado si el monitor de la cocina se ve desde el salón. Enciéndelo si la cocina está cerrada y te sirve que consulten el menú.',
  },
  {
    clave: 'cajaCancela',
    etiqueta: 'La caja puede cancelar pedidos',
    consecuencia: 'La cajera podrá cancelar un pedido o quitar un plato. En ambos casos se le sigue pidiendo tu PIN y queda registrado con su nombre.',
    cuando: 'Encendido está bien para el día a día. Apágalo si prefieres que cada cancelación pase por ti.',
  },
];

// ─────────────────────────────────────────────────────────────
// Consulta
// ─────────────────────────────────────────────────────────────

/** La sección de un canal, o `null` si no está declarada (⇒ sólo dueño). */
export function seccionDeCanal(canal: string): Seccion | null {
  const seccion = canal.split(':')[0];
  return (SECCIONES_DECLARADAS as readonly string[]).includes(seccion)
    ? (seccion as Seccion)
    : null;
}

/**
 * ¿Este oficio puede usar este canal?
 *
 * **Fail-closed en dos niveles:** un rol desconocido no puede nada, y un canal cuya
 * sección no esté declarada sólo lo puede el dueño.
 */
export function puedeUsarCanal(
  rol: string | null | undefined,
  canal: string,
  ajustes: AjustesPermisos = AJUSTES_POR_DEFECTO,
): boolean {
  if (!rol) return false;
  if (rol === 'dueño') return true;
  if (!(ROLES as readonly string[]).includes(rol)) return false;
  if (seccionDeCanal(canal) === null) return false;

  /**
   * Sprint 64 — Los tres ajustes del negocio, aplicados antes de la matriz. Cada uno sólo
   * puede tocar los canales que declara `CANALES_AJUSTE`: un ajuste no puede abrir nada
   * que no esté escrito aquí, así que la superficie sigue siendo auditable.
   */
  for (const clave of Object.keys(CANALES_AJUSTE) as Array<keyof AjustesPermisos>) {
    const regla = CANALES_AJUSTE[clave];
    if (regla.rol !== rol || !regla.canales.includes(canal)) continue;
    return ajustes[clave];
  }

  const reglas = MATRIZ[rol as Rol];
  const seccion = canal.split(':')[0];
  return reglas.some((r) => r === '*' || r === canal || r === `${seccion}:*`);
}

/** Los canales que un oficio puede usar. Para la tabla revisable y los tests. */
export function canalesDe(rol: Rol, ajustes: AjustesPermisos = AJUSTES_POR_DEFECTO): string[] {
  return Object.values(IPC_CHANNELS)
    .filter((c) => puedeUsarCanal(rol, c, ajustes))
    .sort();
}

/** Canales cuya sección no está declarada: hoy sólo el dueño los alcanza. */
export function canalesSinDeclarar(): string[] {
  return Object.values(IPC_CHANNELS)
    .filter((c) => seccionDeCanal(c) === null)
    .sort();
}
